import React, { memo, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { PickInfoFragment } from '@/api/entries/query.generated';
import {
    PlayerInfoFragment,
    PlayerProjectionFragment,
    TeamInfoWithPlayersFragment,
} from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import EdgeComboIcon from '@/assets/icons/edge-combo-icon.js';
import { LineSeparator } from '@/components/LineSeparator';
import { Progressbar } from '@/components/ProgressBar';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { PlayerProfileImage } from '@/components/player-profile/PlayerProfileImage';
import { useEdgeCombo } from '@/feature/betslip-pickem/components/EdgeComboModalProvider';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { pickStatusIconMap } from '@/feature/entries-pickem/components/pick-status-icons';
import { PlayerDescription, PlayerProjectionDetails } from '@/feature/lobby/components/PlayerDetails';
import { designSystem } from '@/styles/styles';
import { EventStatus, League, Outcome, ProjectionType, Result, VoidType } from '@/types/api.generated';
import { getPlayerJerseyNumber } from '@/utils/formatPlayerInfo';
import { gameUtils } from '@/utils/games';
import { getPlayerLogoDetails } from '@/utils/get-player-logo-details';
import { PickStatus, getPickStatus } from '@/utils/pick-status';
import { useShallow } from 'zustand/react/shallow';

type Props = {
    team?: TeamInfoWithPlayersFragment;
    player: PlayerInfoFragment;
    outcome: Outcome;
    animateProgressBars: boolean;
    teamId?: string;
    eventStatus: EventStatus;
    eventLeague: League;
    currentValue?: number | null;
    result?: Result;
    projection: PlayerProjectionFragment;
    isLast: boolean;
    isFirst: boolean;
    isEdgeCombo?: boolean;
    edgeComboData?: { pick: PickInfoFragment; event?: EventInfo }[];
    voidType?: VoidType;
    isShareableContent?: boolean;
    backgroundColor?: string;
    borderColor?: string;
    imageBackgroundColor?: 'background.neutral.subtler' | 'background.neutral.subtle';
    onImageLoad?: () => void;
    liveScoringDisabled?: boolean;
    hideCrossedOutValue?: boolean;
};

export const EntryPickPlayerCard = memo(
    ({
        player,
        outcome,
        animateProgressBars,
        team,
        eventStatus,
        eventLeague,
        currentValue,
        result,
        projection,
        isLast,
        isFirst,
        isEdgeCombo,
        edgeComboData,
        voidType,
        isShareableContent,
        backgroundColor,
        imageBackgroundColor = ds.colorToken.background.neutral.subtler,
        borderColor,
        onImageLoad,
        liveScoringDisabled,
        hideCrossedOutValue,
    }: Props) => {
        const status = getPickStatus({ result, voidType });
        const isStandardVoid = status === PickStatus.VOID;
        const hasStarted = eventStatus === EventStatus.InProgress;
        const hasEnded = gameUtils.hasEnded(eventStatus);
        const { showAcceptEdgeComboPairModal } = useEdgeCombo();

        const { leagueIcon, leagueColor } = useLeagueConfigsStore(useShallow(leagueConfigSelector(eventLeague)));
        const { logoText, teamLogo } = useMemo(
            () => getPlayerLogoDetails(player, eventLeague, team),
            [player, eventLeague, team]
        );
        const playerNumber = getPlayerJerseyNumber(eventLeague, player?.jerseyNumber);

        const playerImageOpacity = isStandardVoid ? 0.4 : 1;

        const max =
            projection.type !== ProjectionType.Regular && projection.nonRegularValue
                ? projection.nonRegularValue
                : projection.value;

        const handlePress = () => {
            if (edgeComboData) {
                const data = edgeComboData.map(it => {
                    const pickTeam = eventUtils.getTeamById(it.event, it.pick.teamId);
                    return {
                        ...it.pick,
                        teamColor: pickTeam?.color ?? '',
                        teamIcon: pickTeam?.icon ?? '',
                    };
                });
                if (data.length === 2) {
                    showAcceptEdgeComboPairModal({
                        edgeCombo: [data[0], data[1]],
                        displayMode: 'info',
                    });
                }
            }
        };

        const firstItemStyle = isShareableContent ? styles.firstShareableCard : styles.firstCard;
        const playerProfileImageStyle = useMemo(() => ({ opacity: playerImageOpacity }), [playerImageOpacity]);

        return (
            <View
                style={[
                    styles.card,
                    isLast ? styles.lastCard : undefined,
                    isFirst ? firstItemStyle : undefined,
                    { backgroundColor },
                    { borderColor },
                ]}
            >
                <Row alignItems="center" gap="s16">
                    <PlayerProfileImage
                        playerImageUrl={player.icon}
                        teamImageUrl={teamLogo ?? leagueIcon}
                        teamColor={team?.color ?? leagueColor}
                        teamSecondaryColor={team?.secondaryColor}
                        logoText={logoText}
                        style={playerProfileImageStyle}
                        playerNumber={playerNumber}
                        playerId={player.id}
                        bgColor={imageBackgroundColor}
                        onLoadImage={onImageLoad}
                    />
                    <Box flex={1}>
                        <Row gap="s8" alignItems="center">
                            <Box style={styles.playerDetails} flex={1}>
                                <PlayerDescription
                                    player={player}
                                    teamName={team?.name || ''}
                                    isVoided={isStandardVoid}
                                    eventLeague={eventLeague}
                                />
                                <PlayerProjectionDetails
                                    selectedProjection={projection}
                                    isVoided={isStandardVoid}
                                    outcome={outcome}
                                    triggerEducationalSheet
                                    hideCrossedOutValue={hideCrossedOutValue}
                                />
                            </Box>
                            <Box width={24} alignItems={'center'}>
                                {pickStatusIconMap[status] ?? (
                                    <View style={styles.progressCircle} testID={'progress-circle'} />
                                )}
                            </Box>
                        </Row>
                        <Box height={20} justifyContent="center" pointerEvents="none">
                            <Progressbar
                                value={currentValue}
                                max={max}
                                animated={animateProgressBars}
                                result={result}
                                voidType={voidType}
                                outcome={outcome}
                                isLiveSportWithoutTracking={liveScoringDisabled || false}
                                loading={Boolean(
                                    !isStandardVoid &&
                                        !liveScoringDisabled &&
                                        (hasStarted || hasEnded) &&
                                        (currentValue === null || currentValue === undefined)
                                )}
                            />
                        </Box>
                    </Box>
                </Row>
                {!isLast ? (
                    <Row height={16} marginVertical="s8" alignItems="center">
                        {isEdgeCombo ? (
                            <Row alignItems={'center'}>
                                <TouchableOpacity onPress={handlePress} style={styles.edgeComboIcon}>
                                    <EdgeComboIcon color={designSystem.colors.neutral300} width={16} height={16} />
                                </TouchableOpacity>
                                {!isShareableContent ? (
                                    <Box flex={1}>
                                        <LineSeparator />
                                    </Box>
                                ) : null}
                            </Row>
                        ) : !isShareableContent ? (
                            <Box flex={1}>
                                <LineSeparator style={styles.lineSeparator} />
                            </Box>
                        ) : null}
                    </Row>
                ) : null}
            </View>
        );
    }
);

const styles = StyleSheet.create({
    progressCircle: {
        borderRadius: 100,
        width: 20,
        height: 20,
        borderWidth: 2,
        borderColor: ds.colorValue.icon.tertiary,
    },
    card: {
        paddingHorizontal: 16,
        borderLeftWidth: 1,
        borderRightWidth: 1,
    },
    lastCard: {
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 16,
        marginBottom: 16,
        borderCurve: 'continuous',
    },
    firstCard: {
        paddingTop: 16,
    },
    firstShareableCard: {
        paddingTop: 16,
        borderTopRightRadius: 16,
        borderTopLeftRadius: 16,
        borderTopWidth: 1,
    },
    lineSeparator: {
        width: '100%',
    },
    playerDetails: {
        marginBottom: 3.5,
    },
    edgeComboIcon: {
        paddingLeft: 16,
        marginRight: 32,
    },
});
